import React from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { PersonalDataFormContainer } from './personalDataForm.styles';
import input from '@single-platform/components/dist/components/input/input';
import { Field, Fieldset, Loader } from '@single-platform/components';
import { useFormValidation } from '@containers/ApplicationKYC/LegalEntityQuestionnaire/validation/useFormValidation';
import MobileInput from '@containers/ApplicationKYC/LegalEntityQuestionnaire/components/phoneInput/mobileInput';
import { FormButton } from '@containers/Auth/components/countryForm/countryForm.styles';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useBiometricLabels } from '@hooks/useBiometricLabels';

const createMultiLineJsx = (text) => {
  return (
    <>
      {text.split('\n').map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    </>
  );
};

const PersonalDataForm = ({ displayPhoneInput, onSubmit, isBiometric, email }) => {
  const { t } = useTranslation();
  const { fieldLabel, fieldTooltip, isLoading } = useBiometricLabels(email, isBiometric);

  const initialValues = {
    customerId: '',
    phone: '',
    validatePhoneInput: displayPhoneInput,
    isValidPhoneNumber: true
  };

  const validationSchema = Yup.object({
    customerId: Yup.string()
      .matches(/^[^\s]+$/, 'validation_customer_id_is_not_valid')
      .required(t('validation_required_field')),
    phone: Yup.string().when('validatePhoneInput', function (validatePhoneInput) {
      if (validatePhoneInput) {
        return Yup.string()
          .required(t('validation_required_field'))
          .test('isValidPhoneNumber', t('validation_incorrect_number'), (value) => {
            return value ? isPossiblePhoneNumber(value) : false;
          });
      } else {
        return Yup.string().optional();
      }
    })
  });
  const disabled = !onSubmit;

  const { validationHelpers, getValues, setTouchedOnAll, handleSubmit } = useFormValidation({
    initialValues,
    validationSchema
  });

  const onBlurHandler = (isValid) => {
    initialValues.isValidPhoneNumber = isValid;
  };

  const onHandleSubmit = async () => {
    await handleSubmit(
      (data) => onSubmit?.(data),
      // on invalid case
      (data) => {
        setTouchedOnAll();
      }
    )();
  };

  if (isLoading && isBiometric) {
    return <Loader />;
  }

  return (
    <PersonalDataFormContainer>
      <Fieldset>
        <div style={{ position: 'relative' }}>
          <Field
            Component={input}
            name="customerId"
            validateOnChange
            value={getValues('customerId')}
            placeholder={isBiometric ? fieldLabel : t('question_person_code')}
            validationHelpers={validationHelpers}
            required
            style={{ width: '100%' }}
          />
          {isBiometric && (
            <Tooltip title={createMultiLineJsx(fieldTooltip)}>
              <InfoCircleOutlined
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '45%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  color: '#999',
                  fontSize: '18px'
                }}
              />
            </Tooltip>
          )}
        </div>

        {displayPhoneInput && (
          <MobileInput
            name="phone"
            value={getValues('phone')}
            validateOnBlur
            required
            onBlurHandler={onBlurHandler}
            placeholder={t('question_address_phone')}
            validationHelpers={validationHelpers}
          />
        )}
      </Fieldset>
      <FormButton disabled={disabled} primary onClick={onHandleSubmit}>
        {t('flow_selection_button')}
      </FormButton>
    </PersonalDataFormContainer>
  );
};

export default PersonalDataForm;
